/**
 * Discovery-Universum — die Welt außerhalb der Watchlist (Stufe A „Entdecken").
 *
 * User-Vision: „Die App soll finden, was ich noch nicht kenne — den nächsten
 * Trend, bevor er gelaufen ist." Der Nachtlauf scannt diese ~50 liquiden
 * US-gelisteten ETFs (Sektoren, Themen wie Raumfahrt/Uran, Regionen bis
 * Vietnam/Afrika, Rohstoffe, Risiko-Pulse) mit der normalen Score-Pipeline +
 * RS-Wende und legt Treffer als Vorschläge in die Entdeckt-Inbox.
 *
 * Kuratiert, bewusst OHNE die typischen Watchlist-Kerne (Doppelte filtert der
 * Cron ohnehin gegen die Watchlist).
 */

export type DiscoveryGruppe = 'sektor' | 'thema' | 'region' | 'rohstoff' | 'anleihe';

export interface DiscoveryEintrag {
  symbol: string;
  label: string;
  gruppe: DiscoveryGruppe;
  /** Steuert die Strategie-Zuordnung der Score-Pipeline. */
  asset_subtype: 'sector_etf' | 'index_etf' | 'gold';
}

export const DISCOVERY_UNIVERSE: DiscoveryEintrag[] = [
  // — Themen (Frühzykliker der Erzählungen: KI, Raumfahrt, Energie-Wende …)
  { symbol: 'SMH', label: 'Halbleiter / KI', gruppe: 'thema', asset_subtype: 'sector_etf' },
  { symbol: 'IGV', label: 'Software', gruppe: 'thema', asset_subtype: 'sector_etf' },
  { symbol: 'XBI', label: 'Biotech', gruppe: 'thema', asset_subtype: 'sector_etf' },
  { symbol: 'ITA', label: 'Rüstung / Raumfahrt (USA)', gruppe: 'thema', asset_subtype: 'sector_etf' },
  { symbol: 'ARKX', label: 'Raumfahrt / Space', gruppe: 'thema', asset_subtype: 'sector_etf' },
  { symbol: 'TAN', label: 'Solar', gruppe: 'thema', asset_subtype: 'sector_etf' },
  { symbol: 'ICLN', label: 'Erneuerbare Energie', gruppe: 'thema', asset_subtype: 'sector_etf' },
  { symbol: 'URA', label: 'Uran / Atomkraft', gruppe: 'thema', asset_subtype: 'sector_etf' },
  { symbol: 'LIT', label: 'Lithium / Batterien', gruppe: 'thema', asset_subtype: 'sector_etf' },
  { symbol: 'PAVE', label: 'US-Infrastruktur', gruppe: 'thema', asset_subtype: 'sector_etf' },
  { symbol: 'IHI', label: 'Medizintechnik', gruppe: 'thema', asset_subtype: 'sector_etf' },
  { symbol: 'JETS', label: 'Airlines', gruppe: 'thema', asset_subtype: 'sector_etf' },
  // — Sektoren (Branchen-Pulse, die in der 8er-SPDR-Sicht fehlen)
  { symbol: 'KRE', label: 'Regionalbanken', gruppe: 'sektor', asset_subtype: 'sector_etf' },
  { symbol: 'ITB', label: 'Hausbau', gruppe: 'sektor', asset_subtype: 'sector_etf' },
  { symbol: 'XRT', label: 'Einzelhandel', gruppe: 'sektor', asset_subtype: 'sector_etf' },
  { symbol: 'IYT', label: 'Transport', gruppe: 'sektor', asset_subtype: 'sector_etf' },
  { symbol: 'VNQ', label: 'Immobilien (REITs)', gruppe: 'sektor', asset_subtype: 'sector_etf' },
  { symbol: 'OIH', label: 'Öl-Service', gruppe: 'sektor', asset_subtype: 'sector_etf' },
  { symbol: 'FCG', label: 'Erdgas-Produzenten', gruppe: 'sektor', asset_subtype: 'sector_etf' },
  { symbol: 'XME', label: 'Minen & Metalle', gruppe: 'sektor', asset_subtype: 'sector_etf' },
  { symbol: 'GDX', label: 'Goldminen', gruppe: 'sektor', asset_subtype: 'gold' },
  { symbol: 'COPX', label: 'Kupferminen', gruppe: 'sektor', asset_subtype: 'sector_etf' },
  // — Regionen (wo auf der Welt dreht Geld hin?)
  { symbol: 'FXI', label: 'China (Large Caps)', gruppe: 'region', asset_subtype: 'index_etf' },
  { symbol: 'KWEB', label: 'China Internet', gruppe: 'region', asset_subtype: 'sector_etf' },
  { symbol: 'INDA', label: 'Indien', gruppe: 'region', asset_subtype: 'index_etf' },
  { symbol: 'EEM', label: 'Schwellenländer', gruppe: 'region', asset_subtype: 'index_etf' },
  { symbol: 'ILF', label: 'Südamerika', gruppe: 'region', asset_subtype: 'index_etf' },
  { symbol: 'EWZ', label: 'Brasilien', gruppe: 'region', asset_subtype: 'index_etf' },
  { symbol: 'EWW', label: 'Mexiko', gruppe: 'region', asset_subtype: 'index_etf' },
  { symbol: 'ARGT', label: 'Argentinien', gruppe: 'region', asset_subtype: 'index_etf' },
  { symbol: 'AFK', label: 'Afrika', gruppe: 'region', asset_subtype: 'index_etf' },
  { symbol: 'EZA', label: 'Südafrika', gruppe: 'region', asset_subtype: 'index_etf' },
  { symbol: 'VGK', label: 'Europa', gruppe: 'region', asset_subtype: 'index_etf' },
  { symbol: 'EWG', label: 'Deutschland', gruppe: 'region', asset_subtype: 'index_etf' },
  { symbol: 'EWP', label: 'Spanien', gruppe: 'region', asset_subtype: 'index_etf' },
  { symbol: 'EPOL', label: 'Polen', gruppe: 'region', asset_subtype: 'index_etf' },
  { symbol: 'EWJ', label: 'Japan', gruppe: 'region', asset_subtype: 'index_etf' },
  { symbol: 'EWY', label: 'Südkorea', gruppe: 'region', asset_subtype: 'index_etf' },
  { symbol: 'EWT', label: 'Taiwan', gruppe: 'region', asset_subtype: 'index_etf' },
  { symbol: 'VNM', label: 'Vietnam', gruppe: 'region', asset_subtype: 'index_etf' },
  { symbol: 'EIS', label: 'Israel', gruppe: 'region', asset_subtype: 'index_etf' },
  { symbol: 'EWA', label: 'Australien', gruppe: 'region', asset_subtype: 'index_etf' },
  // — Rohstoffe (direkte Preise)
  { symbol: 'GLD', label: 'Gold', gruppe: 'rohstoff', asset_subtype: 'gold' },
  { symbol: 'SLV', label: 'Silber', gruppe: 'rohstoff', asset_subtype: 'gold' },
  { symbol: 'PPLT', label: 'Platin', gruppe: 'rohstoff', asset_subtype: 'gold' },
  { symbol: 'CPER', label: 'Kupfer', gruppe: 'rohstoff', asset_subtype: 'sector_etf' },
  { symbol: 'USO', label: 'Öl (WTI)', gruppe: 'rohstoff', asset_subtype: 'sector_etf' },
  { symbol: 'UNG', label: 'Erdgas', gruppe: 'rohstoff', asset_subtype: 'sector_etf' },
  { symbol: 'DBA', label: 'Agrar-Rohstoffe', gruppe: 'rohstoff', asset_subtype: 'sector_etf' },
  // — Anleihen / Risiko-Pulse
  { symbol: 'TLT', label: 'US-Anleihen lang', gruppe: 'anleihe', asset_subtype: 'index_etf' },
  { symbol: 'HYG', label: 'High-Yield (Risikopuls)', gruppe: 'anleihe', asset_subtype: 'index_etf' },
  { symbol: 'EMB', label: 'EM-Staatsanleihen', gruppe: 'anleihe', asset_subtype: 'index_etf' },
];

export const GRUPPE_LABEL: Record<DiscoveryGruppe, string> = {
  sektor: 'Sektor',
  thema: 'Thema',
  region: 'Region',
  rohstoff: 'Rohstoff',
  anleihe: 'Anleihen',
};

/**
 * Audit 13.07. — beim 1-Klick-Übernehmen den passenden Watchlist-Layer
 * vorwählen, statt den gerade offenen Tab zu erben (ein Rohstoff-ETF landete
 * sonst im Core-„Quality"-Bucket). Alle Discovery-Werte sind ETFs.
 */
export function discoveryTargetLayer(gruppe: DiscoveryGruppe): string {
  switch (gruppe) {
    case 'thema':
      return 'theme';
    case 'anleihe':
      return 'defensive';
    case 'sektor':
    case 'region':
    case 'rohstoff':
    default:
      return 'tactical';
  }
}
